import {
    type Dispatch,
    type SetStateAction,
    useCallback,
    useEffect,
    useMemo,
} from "react";
import type { RowSelectionState } from "@tanstack/react-table";
import { useCloseTemplates } from "@api/closeTemplate.api";
import { useOrganizationCloseStages } from "@api/organizationCloseStage.api";
import type { ClientsCloseOverviewSlimQuery } from "@common/schemas/clientsCloseOverview.schema";
import type {
    ClientsWorkOverviewSortValue,
    ClientsWorkOverviewView,
} from "@common/types/views/clientsWorkOverviewView";
import { BoardLayoutToggle } from "@components/views/board/BoardLayoutToggle";
import { createIsPlacementShown } from "@design-system/Board/boardVisibility";
import type { BoardLayout } from "@components/views/board/boardLayoutCache";
import type { ClientsWorkOverviewWorkspaceRow } from "../clientsWorkOverviewViewConfig";
import type { GroupBy } from "../useGroupedTableData";
import { isWorkPersonRole } from "../workPersonGroups";
import { getWorkBoardColumnBy, getWorkRows } from "./workBoardColumns";
import {
    getWorkBoardAxisFields,
    getWorkBoardLaneBy,
    getWorkBoardModel,
} from "./workBoardLanes";
import { getWorkBoardColumnIcon } from "./workBoardStatus";
import {
    getWorkViewBoardDisplay,
    useWorkViewCardFieldIds,
} from "./workViewBoardDisplay";
import { getWorkCardFieldColumns } from "./workCardFieldColumns";
import { useWorkBoardCache } from "./useWorkBoardCache";
import { useWorkBoardCollapse } from "./useWorkBoardCollapse";
import { useLiveMovedCardIds } from "./useLiveMovedCardIds";
import { useWorkBoardSelection } from "./useWorkBoardSelection";
import type { WorkBoardProps } from "./WorkBoard";
import { WorkBoardDisplayButton } from "./WorkBoardDisplayButton";

interface UseWorkBoardLayoutArgs {
    view: ClientsWorkOverviewView;
    /** The picked view's id, which keeps the empty columns expanded this session; null with none picked. */
    viewId: string | null;
    setView: (
        update: (prev: ClientsWorkOverviewView) => ClientsWorkOverviewView,
    ) => void;
    groupBy: GroupBy;
    /** List's sort, which the gear shows and changes. */
    sort: ClientsWorkOverviewSortValue;
    onSortChange: (sort: ClientsWorkOverviewSortValue) => void;
    /** The page's clients with their applications; null until both first arrive. */
    workspaceRows: ClientsWorkOverviewWorkspaceRow[] | null;
    /** The query `workspaceRows` came from: filters, search, sort and page. */
    listQuery: ClientsCloseOverviewSlimQuery;
    /** Runs on every layout switch, e.g. to clear the row selection. */
    onLayoutChange: () => void;
    /** List's row selection, which the board's card selection is. */
    rowSelection: RowSelectionState;
    onRowSelectionChange: Dispatch<SetStateAction<RowSelectionState>>;
}

/** What the Work board shows; `board` is null while its data loads. */
export interface WorkBoardState {
    board: Omit<
        WorkBoardProps,
        "sort" | "callbacks" | "activeItemId" | "onOpenItem"
    > | null;
}

/**
 * The Work page's layout: the List/Board toggle and, on Board, the gear for
 * the view toolbar, and the board's columns, lanes and cards. The layout is
 * personal (never in the view), so switching keeps the selected view.
 */
export const useWorkBoardLayout = ({
    view,
    viewId,
    setView,
    groupBy,
    sort,
    onSortChange,
    workspaceRows,
    listQuery,
    onLayoutChange,
    rowSelection,
    onRowSelectionChange,
}: UseWorkBoardLayoutArgs) => {
    const { layout, collapsed, setLayout, updateCollapsed } =
        useWorkBoardCache();
    const isBoardLayout = layout === "board";
    const columnBy = getWorkBoardColumnBy(groupBy);
    const { laneBy: storedLaneBy, isEmptyColumnsShown } =
        getWorkViewBoardDisplay(view.options);
    const laneBy = isWorkPersonRole(groupBy)
        ? groupBy
        : getWorkBoardLaneBy({ storedLaneBy, columnBy });
    const closeStatuses = listQuery.filters?.closeStatuses;
    const groupings = [columnBy, laneBy];

    const { data: stages, isLoading: areStagesLoading } =
        useOrganizationCloseStages({
            enabled: isBoardLayout && groupings.includes("stage"),
        });
    const { data: templates, isLoading: areTemplatesLoading } =
        useCloseTemplates({
            enabled: isBoardLayout && groupings.includes("template"),
        });
    const isGroupingDataLoading = areStagesLoading || areTemplatesLoading;
    const workRows = useMemo(
        () => getWorkRows(workspaceRows ?? []),
        [workspaceRows],
    );
    const { selectedItemIds, onSelectionChange, dropHiddenRows } =
        useWorkBoardSelection({
            cardRows: workRows,
            rowSelection,
            onRowSelectionChange,
        });

    const handleLayoutChange = useCallback(
        (nextLayout: BoardLayout) => {
            setLayout(nextLayout);
            onLayoutChange();
        },
        [setLayout, onLayoutChange],
    );

    const viewCardFieldIds = useWorkViewCardFieldIds({
        options: view.options,
    });
    const model = useMemo(() => {
        if (!isBoardLayout || workspaceRows === null || isGroupingDataLoading) {
            return null;
        }
        return {
            ...getWorkBoardModel({
                columnBy,
                laneBy,
                workspaceRows,
                stages: stages ?? [],
                templateNames: new Map(
                    (templates ?? []).map(({ id, name }) => [id, name]),
                ),
                isEmptyColumnsShown,
                getIcon: getWorkBoardColumnIcon,
                closeStatuses,
                personLaneTitle: "details",
            }),
            rows: workRows,
        };
    }, [
        isBoardLayout,
        workspaceRows,
        workRows,
        isGroupingDataLoading,
        columnBy,
        laneBy,
        stages,
        templates,
        isEmptyColumnsShown,
        closeStatuses,
    ]);
    const collapse = useWorkBoardCollapse({
        viewId,
        collapsed,
        updateCollapsed,
        board: model,
    });

    const cards = useMemo(
        () =>
            model && {
                ...model,
                fieldColumns: getWorkCardFieldColumns({
                    columns: view.columns,
                    cardFieldIds: viewCardFieldIds,
                    hiddenFields: getWorkBoardAxisFields({ columnBy, laneBy }),
                }),
                isClientShown: columnBy !== "client" && laneBy !== "client",
                ...collapse,
            },
        [model, view.columns, viewCardFieldIds, columnBy, laneBy, collapse],
    );

    useEffect(() => {
        if (cards) {
            dropHiddenRows({
                rows: cards.rows,
                getPlacements: cards.getPlacements,
                isPlacementShown: createIsPlacementShown(cards),
            });
        }
    }, [cards, dropHiddenRows]);

    const scope = useMemo(
        () => ({ listQuery, columnBy, laneBy }),
        [listQuery, columnBy, laneBy],
    );
    const enteringItemIds = useLiveMovedCardIds({ scope, cards });

    const board = useMemo(
        (): WorkBoardState["board"] =>
            cards && {
                ...cards,
                selectedItemIds,
                onSelectionChange,
                enteringItemIds,
            },
        [cards, selectedItemIds, onSelectionChange, enteringItemIds],
    );

    const toolbarControls = useMemo(
        () => (
            <>
                <BoardLayoutToggle
                    layout={layout}
                    onLayoutChange={handleLayoutChange}
                />
                {isBoardLayout && (
                    <WorkBoardDisplayButton
                        view={view}
                        onViewChange={setView}
                        groupBy={groupBy}
                        columnBy={columnBy}
                        laneBy={laneBy}
                        isEmptyColumnsShown={isEmptyColumnsShown}
                        sort={sort}
                        onSortChange={onSortChange}
                    />
                )}
            </>
        ),
        [
            isBoardLayout,
            layout,
            handleLayoutChange,
            view,
            setView,
            groupBy,
            columnBy,
            laneBy,
            isEmptyColumnsShown,
            sort,
            onSortChange,
        ],
    );

    return { isBoardLayout, toolbarControls, board };
};
